import axios from 'axios';
import { api } from './api/client';
import { platformStorage } from '../utils/storage';
import { API_BASE_URL } from '../config/api';
import logger from '../utils/logger';

// Types
export interface TotpSetupResponse {
  secret: string;
  qrCodeUrl: string;
  backupCodes: string[];
}

export interface TwoFactorStatus {
  totpEnabled: boolean;
  emailOtpEnabled: boolean;
  totpSetupAt: string | null;
  emailOtpSetupAt: string | null;
  backupCodesRemaining: number;
}

export interface VerifyCodePayload {
  code: string;
  method: 'totp' | 'email' | 'backup';
}

class TwoFactorService {
  private async getAuthToken(): Promise<string> {
    const token = await platformStorage.getItem('access_token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    return token;
  }

  /**
   * 1. Start TOTP Setup - Get QR code and secret
   */
  async startTotpSetup(): Promise<TotpSetupResponse> {
    try {
      const response = await api.post('/auth/2fa/totp/setup', {});
      return response.data;
    } catch (error: any) {
      logger.error('Start TOTP setup error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to start TOTP setup');
    }
  }

  /**
   * 2. Verify and Complete TOTP Setup
   */
  async verifyTotpSetup(code: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/auth/2fa/totp/verify-setup', { token: code });
      return response.data;
    } catch (error: any) {
      logger.error('Verify TOTP setup error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to verify TOTP code');
    }
  }

  /**
   * 3. Disable TOTP
   */
  async disableTotp(password: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/auth/2fa/totp/disable', { password });
      return response.data;
    } catch (error: any) {
      logger.error('Disable TOTP error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to disable TOTP');
    }
  }

  /**
   * 4. Enable Email OTP
   */
  async enableEmailOtp(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/auth/2fa/email/enable', {});
      return response.data;
    } catch (error: any) {
      logger.error('Enable Email OTP error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to enable Email OTP');
    }
  }

  /**
   * 5. Disable Email OTP
   */
  async disableEmailOtp(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/auth/2fa/email/disable', {});
      return response.data;
    } catch (error: any) {
      logger.error('Disable Email OTP error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to disable Email OTP');
    }
  }

  /**
   * 6. Send Email OTP Code (Login Flow - uses tempToken)
   */
  async sendEmailOtpForLogin(tempToken: string): Promise<{ success: boolean; message: string }> {
    try {
      logger.log('[sendEmailOtpForLogin] 📧 Sending OTP with tempToken');
      const response = await axios.post(
        `${API_BASE_URL}/auth/send-email-otp`,
        {},
        {
          headers: {
            Authorization: `Bearer ${tempToken}`,
          },
        },
      );
      logger.log('[sendEmailOtpForLogin] ✅ OTP sent successfully');
      return response.data;
    } catch (error: any) {
      logger.error('[sendEmailOtpForLogin] ❌ Error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to send Email OTP');
    }
  }

  /**
   * 6b. Send Email OTP Code (Settings Flow - uses stored JWT)
   */
  async sendEmailOtp(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/auth/2fa/email/send', {});
      return response.data;
    } catch (error: any) {
      logger.error('Send Email OTP error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to send Email OTP');
    }
  }

  /**
   * 7. Verify Email OTP Code
   */
  async verifyEmailOtp(code: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/auth/2fa/email/verify', { code });
      return response.data;
    } catch (error: any) {
      logger.error('Verify Email OTP error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to verify Email OTP');
    }
  }

  /**
   * 8. Get 2FA Status
   */
  async getStatus(): Promise<TwoFactorStatus> {
    try {
      logger.log('🔍 Calling API endpoint: /auth/two-factor/status');
      const response = await api.get('/auth/two-factor/status');
      logger.log('✅ API Response:', response.data);
      return response.data;
    } catch (error: any) {
      logger.error('❌ Get 2FA status error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to get 2FA status');
    }
  }

  /**
   * 8b. Get 2FA Status with Provided Token (for pre-login check)
   */
  async getStatusWithToken(token: string): Promise<TwoFactorStatus> {
    try {
      logger.log('🔍 Calling API endpoint: /auth/two-factor/status (with provided token)');
      const response = await axios.get(`${API_BASE_URL}/auth/two-factor/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      logger.log('✅ API Response:', response.data);
      return response.data;
    } catch (error: any) {
      logger.error('❌ Get 2FA status error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to get 2FA status');
    }
  }

  /**
   * 9. Regenerate Backup Codes
   */
  async regenerateBackupCodes(): Promise<{ backupCodes: string[] }> {
    try {
      logger.log('🔄 Regenerating backup codes...');
      const response = await api.post('/auth/two-factor/backup-codes/regenerate', {});
      logger.log('✅ Backup codes regenerated');
      return response.data;
    } catch (error: any) {
      logger.error('❌ Regenerate backup codes error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to regenerate backup codes');
    }
  }

  /**
   * Helper: Verify code during login (for LoginScreen)
   */
  /**
   * Verify 2FA code during login (TOTP, Email OTP, or Backup Code)
   * @param payload - Code and method
   * @param tempToken - Temporary JWT token from login response (before 2FA verification)
   */
  async verifyLoginCode(payload: VerifyCodePayload, tempToken?: string): Promise<boolean> {
    try {
      // Use provided token or get from storage
      const token = tempToken || (await this.getAuthToken());

      if (payload.method === 'totp' || payload.method === 'backup') {
        // Verify TOTP or backup code
        // Note: Backend expects 'token' parameter, not 'code'
        const response = await axios.post(
          `${API_BASE_URL}/auth/2fa/totp/verify-setup`,
          { token: payload.code }, // ✅ Changed from 'code' to 'token'
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        return response.data.success || true;
      } else if (payload.method === 'email') {
        // Verify Email OTP
        const response = await axios.post(
          `${API_BASE_URL}/auth/2fa/email/verify`,
          { code: payload.code },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        return response.data.success || true;
      } else {
        throw new Error('Invalid verification method');
      }
    } catch (error: any) {
      logger.error('Verify login code error:', error);
      // Provide more detailed error messages
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'Invalid verification code');
      } else if (error.response?.status === 401) {
        throw new Error('Authentication expired. Please login again.');
      } else if (error.response?.status === 429) {
        throw new Error('Too many attempts. Please try again later.');
      } else {
        throw new Error(error.message || 'Failed to verify code');
      }
    }
  }
}

export default new TwoFactorService();
